<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold">Transcript Types</h3>
        <p class="text-sm text-gray-500">Configure the kinds of transcripts applicants can request from your school.</p>
      </div>
      <Button
        v-if="canCreate"
        icon="pi pi-plus"
        label="New Type"
        size="small"
        data-testid="transcript-type-new"
        @click="openCreate"
      />
    </div>

    <div class="bg-white rounded-lg shadow">
      <DataTable
        size="small"
        :value="types"
        :loading="loading"
        stripedRows
        responsiveLayout="scroll"
        dataKey="id"
        :paginator="types.length > 10"
        :rows="10"
      >
        <template #empty>
          <div class="text-center py-6 text-gray-400 text-sm">No transcript types configured.</div>
        </template>

        <Column field="name" header="Name">
          <template #body="{ data }">
            <div class="text-sm font-medium">{{ data.name }}</div>
            <div v-if="data.description" class="text-xs text-gray-500">{{ data.description }}</div>
          </template>
        </Column>
        <Column header="Category">
          <template #body="{ data }">
            <TranscriptCategoryBadge :category="data.category" />
          </template>
        </Column>
        <Column header="Fee">
          <template #body="{ data }">
            <span v-if="data.billItemTemplate">{{ formatCurrency(data.billItemTemplate.default_amount) }}</span>
            <span v-else class="text-xs text-gray-400">Free</span>
          </template>
        </Column>
        <Column header="Active">
          <template #body="{ data }">
            <Tag :value="data.is_active ? 'Active' : 'Inactive'" :severity="data.is_active ? 'success' : 'secondary'" />
          </template>
        </Column>
        <Column header="Actions" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center gap-1">
              <Button
                v-if="canUpdate"
                icon="pi pi-pencil"
                size="small"
                severity="secondary"
                text
                rounded
                v-tooltip.top="'Edit'"
                :data-testid="`transcript-type-edit-${data.id}`"
                @click="openEdit(data)"
              />
              <Button
                v-if="canDelete"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                rounded
                v-tooltip.top="'Delete'"
                :data-testid="`transcript-type-delete-${data.id}`"
                @click="onDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <TranscriptTypeForm v-model="formOpen" :type="editingType" @saved="refetch()" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useConfirm } from 'primevue/useconfirm';
import { useTranscriptTypes } from '@/composables/transcript/useTranscriptTypes';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { formatCurrency } from '@/utils/currency';
import type { TranscriptType } from '@/types/composition-functions';

const { types, loading, refetch } = useTranscriptTypes();
const { deleteTranscriptType } = useTranscriptMutations();

const { currentUser } = storeToRefs(useUserStore());
const confirm = useConfirm();

const canCreate = computed(() => can(currentUser.value, AppPermissions.Transcripts.Actions.Create, AppPermissions.Transcripts.Resource));
const canUpdate = computed(() => can(currentUser.value, AppPermissions.Transcripts.Actions.Update, AppPermissions.Transcripts.Resource));
const canDelete = computed(() => can(currentUser.value, AppPermissions.Transcripts.Actions.Delete, AppPermissions.Transcripts.Resource));

const formOpen = ref(false);
const editingType = ref<TranscriptType | null>(null);

const openCreate = () => {
  editingType.value = null;
  formOpen.value = true;
};

const openEdit = (type: TranscriptType) => {
  editingType.value = type;
  formOpen.value = true;
};

const onDelete = (type: TranscriptType) => {
  confirm.require({
    message: `Delete the transcript type "${type.name}"? This cannot be undone.`,
    header: 'Delete Type',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try { await deleteTranscriptType(type.id); } catch { /* toast shown by composable */ }
    },
  });
};
</script>
